import {
  FilterDocument,
  FilterOptionKeys,
  GroupByMode,
  GroupSetting,
  ID,
  SmartList,
  SortingMode,
  Tag,
  TaskGroupBy,
} from '@griply/types';
import {
  defaultAllTasksFilterDocument,
  inboxFilters,
  inboxGroupOptions,
  inboxSortOptions,
  tagDetailFilters,
  taskListFilters,
} from '@griply/utils';
import {
  useSmartListCustomOrderByGrouping,
  useSmartListFilterDraft,
  useSmartListOnReorder,
  useUpdateSmartList,
} from 'features/smart-lists';
import {
  getDefaultFilterDocument,
  getFilterDocument,
  useUpdateTag,
} from 'features/tags';
import {
  useAllTasksFilterDocument,
  useInboxFilterDocument,
  useUpdateAllTasksFilterDocument,
  useUpdateInboxFilterDocument,
} from 'features/user';
import { useCallback, useMemo } from 'react';
import { FilterDocuments } from 'shared/constants';
import { useAllTasksCustomOrderByGrouping } from 'shared/hooks/use-all-tasks-custom-order-by-grouping';
import { useAllTasksOnReorder } from 'shared/hooks/use-all-tasks-on-reorder';
import { useAllTasksOnSectionCollapse } from 'shared/hooks/use-all-tasks-on-section-collapse';
import { useInboxCustomOrderByGrouping } from 'shared/hooks/use-inbox-custom-order-by-grouping';
import { useInboxOnReorder } from 'shared/hooks/use-inbox-on-reorder';
import { useInboxOnSectionCollapse } from 'shared/hooks/use-inbox-on-section-collapse';

import {
  DefaultSideOptions,
  ExtraSideOptions,
  ViewMode,
} from '../types/side-panel';
import { useHabitsOnReorder } from './use-habits-on-reorder';
import { useSidePanelHabits } from './use-side-panel-habits';

const supportedGroupByModes: TaskGroupBy[] = [
  GroupByMode.None,
  GroupByMode.Goal,
  GroupByMode.LifeArea,
  GroupByMode.Priority,
  GroupByMode.Type,
  GroupByMode.Deadline,
  GroupByMode.DeadlineDeadline,
  GroupByMode.Tag,
];

const habitsSupportedGroupByModes: TaskGroupBy[] = [
  GroupByMode.None,
  GroupByMode.Goal,
  GroupByMode.LifeArea,
  GroupByMode.Priority,
];

const habitsSupportedSortingModes: SortingMode[] = [
  SortingMode.Custom,
  SortingMode.Priority,
];

const habitsSupportedFilters: FilterOptionKeys[] = [];

const inboxSupportedGroupByModes: TaskGroupBy[] = [...inboxGroupOptions];
const inboxSupportedSortingModes: SortingMode[] = [...inboxSortOptions];
const inboxSupportedFilters: FilterOptionKeys[] = [...inboxFilters];

// Read source and write target are paired per viewMode.type — mixing writes to the wrong store.
export const useSidePanelListConfig = (
  viewMode: ViewMode,
  selectedTag: Tag | undefined,
  selectedSmartList: SmartList | undefined,
) => {
  const allTasksFilterDocument = useAllTasksFilterDocument();
  const updateAllTasksFilterDocument = useUpdateAllTasksFilterDocument();
  const inboxFilterDocument = useInboxFilterDocument();
  const updateInboxFilterDocument = useUpdateInboxFilterDocument();

  const { submit: updateTag } = useUpdateTag();
  const { submit: updateSmartList } = useUpdateSmartList();

  const smartListDraft = useSmartListFilterDraft(selectedSmartList);

  const habits = useSidePanelHabits();
  const habitsOnReorder = useHabitsOnReorder(habits.filterDocument.groupByMode);

  const isHabits = viewMode.type === DefaultSideOptions.Habits;
  const isInbox = viewMode.type === DefaultSideOptions.Inbox;
  const isTag = viewMode.type === ExtraSideOptions.Tag;

  const baseFilterDocument = useMemo(
    () =>
      viewMode.type === ExtraSideOptions.Tag
        ? getFilterDocument(selectedTag)
        : viewMode.type === ExtraSideOptions.SmartList
          ? smartListDraft.activeFilterDocument ??
            smartListDraft.defaultFilterDocument
          : viewMode.type === DefaultSideOptions.Inbox
            ? inboxFilterDocument
            : allTasksFilterDocument,
    [
      allTasksFilterDocument,
      inboxFilterDocument,
      selectedTag,
      smartListDraft.activeFilterDocument,
      smartListDraft.defaultFilterDocument,
      viewMode.type,
    ],
  );

  const updateTagFilterDocument = useCallback(
    (newFilterDocument: FilterDocument) =>
      selectedTag &&
      updateTag({
        ...selectedTag,
        filterDocument: newFilterDocument,
      }),
    [selectedTag, updateTag],
  );

  const baseUpdateFilterDocument =
    viewMode.type === ExtraSideOptions.Tag
      ? updateTagFilterDocument
      : viewMode.type === ExtraSideOptions.SmartList
        ? smartListDraft.updateFilterDocument
        : viewMode.type === DefaultSideOptions.Inbox
          ? updateInboxFilterDocument
          : updateAllTasksFilterDocument;

  const allTasksCustomOrder = useAllTasksCustomOrderByGrouping(
    allTasksFilterDocument.groupByMode,
  );
  const updateAllTasksCustomOrder = useAllTasksOnReorder(
    allTasksFilterDocument.groupByMode,
  );

  const smartListCustomOrder = useSmartListCustomOrderByGrouping(
    baseFilterDocument.groupByMode,
    selectedSmartList,
  );
  const updateSmartListCustomOrder = useSmartListOnReorder(
    baseFilterDocument,
    selectedSmartList,
  );

  const inboxCustomOrder = useInboxCustomOrderByGrouping(
    inboxFilterDocument.groupByMode,
  );
  const updateInboxCustomOrder = useInboxOnReorder(
    inboxFilterDocument.groupByMode,
  );

  const baseCustomOrder =
    viewMode.type === DefaultSideOptions.AllTasks
      ? allTasksCustomOrder
      : viewMode.type === ExtraSideOptions.SmartList
        ? smartListCustomOrder
        : viewMode.type === DefaultSideOptions.Inbox
          ? inboxCustomOrder
          : {};

  const baseUpdateCustomOrder =
    viewMode.type === DefaultSideOptions.AllTasks
      ? updateAllTasksCustomOrder
      : viewMode.type === ExtraSideOptions.SmartList
        ? updateSmartListCustomOrder
        : viewMode.type === DefaultSideOptions.Inbox
          ? updateInboxCustomOrder
          : undefined;

  const updateAllTasksCollapsedSections = useAllTasksOnSectionCollapse();
  const updateInboxCollapsedSections = useInboxOnSectionCollapse();

  const updateCollapsedSections = useCallback(
    (collapsedSections: ID[]) => {
      if (viewMode.type === ExtraSideOptions.SmartList) {
        if (!selectedSmartList) {
          return;
        }
        updateSmartList({
          id: selectedSmartList.id,
          filterDocument: {
            ...selectedSmartList.filterDocument,
            collapsedSections,
          },
        });
        return;
      }
      if (viewMode.type === DefaultSideOptions.AllTasks) {
        updateAllTasksCollapsedSections(collapsedSections);
        return;
      }
      if (viewMode.type === DefaultSideOptions.Inbox) {
        updateInboxCollapsedSections(collapsedSections);
      }
    },
    [
      viewMode.type,
      selectedSmartList,
      updateSmartList,
      updateAllTasksCollapsedSections,
      updateInboxCollapsedSections,
    ],
  );

  const baseDefaultFilterDocument = useMemo(
    () =>
      viewMode.type === ExtraSideOptions.Tag
        ? getDefaultFilterDocument(selectedTag)
        : viewMode.type === ExtraSideOptions.SmartList
          ? smartListDraft.defaultFilterDocument
          : viewMode.type === DefaultSideOptions.AllTasks
            ? defaultAllTasksFilterDocument
            : FilterDocuments.Inbox.defaultFilterDocument,
    [selectedTag, smartListDraft.defaultFilterDocument, viewMode.type],
  );

  const baseOnReset = useCallback(
    () =>
      viewMode.type === ExtraSideOptions.SmartList
        ? smartListDraft.reset()
        : baseUpdateFilterDocument(baseDefaultFilterDocument),
    [
      baseDefaultFilterDocument,
      smartListDraft,
      baseUpdateFilterDocument,
      viewMode.type,
    ],
  );

  const groupSettingOverrides: GroupSetting | undefined = isHabits
    ? { hideNewTaskRow: true }
    : undefined;

  return {
    filterDocument: isHabits ? habits.filterDocument : baseFilterDocument,
    defaultFilterDocument: isHabits
      ? habits.defaultFilterDocument
      : baseDefaultFilterDocument,
    updateFilterDocument: isHabits
      ? habits.updateFilterDocument
      : baseUpdateFilterDocument,
    customOrder: isHabits ? habits.customOrder : baseCustomOrder,
    updateCustomOrder: isHabits ? habitsOnReorder : baseUpdateCustomOrder,
    onReset: isHabits ? habits.onReset : baseOnReset,
    onSectionCollapse: isHabits ? undefined : updateCollapsedSections,
    supportedGroupByModes: isHabits
      ? habitsSupportedGroupByModes
      : isInbox
        ? inboxSupportedGroupByModes
        : supportedGroupByModes,
    supportedSortingModes: isHabits
      ? habitsSupportedSortingModes
      : isInbox
        ? inboxSupportedSortingModes
        : undefined,
    supportedFilters: isHabits
      ? habitsSupportedFilters
      : isInbox
        ? inboxSupportedFilters
        : isTag
          ? tagDetailFilters
          : taskListFilters,
    groupSettingOverrides,
    onSaveSmartList: smartListDraft.save,
  };
};
